/**
 * COLOR SAMPLER COMPONENT
 * 
 * Inline color sampling for Add modals.
 * Click on uploaded photos to sample colors.
 * 
 * Simplified version of ColorTaggerModal for use in forms.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Pipette, Trash2, RotateCcw } from 'lucide-react';

interface ColorSamplerProps {
  photoUrl: string;
  colors: string[];
  onChange: (colors: string[]) => void;
}

// Convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
};

export default function ColorSampler({ photoUrl, colors, onChange }: ColorSamplerProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const SAMPLE_RADIUS = 12;
  
  // Load image onto canvas
  useEffect(() => {
    if (!photoUrl || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    setImageLoaded(false);
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const maxWidth = 300;
      const maxHeight = 200;
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      setImageLoaded(true);
    };
    
    img.onerror = () => {
      console.error('Failed to load image for color sampling');
      setImageLoaded(false);
    };
    
    img.src = photoUrl;
  }, [photoUrl]);
  
  // Handle click to sample color
  const handleImageClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const centerX = Math.floor((e.clientX - rect.left) * scaleX);
    const centerY = Math.floor((e.clientY - rect.top) * scaleY);
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Sample a circular area and average
    let totalR = 0, totalG = 0, totalB = 0;
    let pixelCount = 0;
    
    const startX = Math.max(0, centerX - SAMPLE_RADIUS);
    const startY = Math.max(0, centerY - SAMPLE_RADIUS);
    const width = Math.min(SAMPLE_RADIUS * 2, canvas.width - startX);
    const height = Math.min(SAMPLE_RADIUS * 2, canvas.height - startY);
    
    const imageData = ctx.getImageData(startX, startY, width, height);
    const data = imageData.data;
    
    for (let dy = -SAMPLE_RADIUS; dy <= SAMPLE_RADIUS; dy++) {
      for (let dx = -SAMPLE_RADIUS; dx <= SAMPLE_RADIUS; dx++) {
        if (dx * dx + dy * dy <= SAMPLE_RADIUS * SAMPLE_RADIUS) {
          const px = centerX + dx;
          const py = centerY + dy;
          
          if (px >= 0 && px < canvas.width && py >= 0 && py < canvas.height) {
            const localX = px - startX;
            const localY = py - startY;
            const idx = (localY * width + localX) * 4;
            
            if (idx >= 0 && idx < data.length - 2) {
              totalR += data[idx];
              totalG += data[idx + 1];
              totalB += data[idx + 2];
              pixelCount++;
            }
          }
        }
      }
    }
    
    if (pixelCount === 0) return;
    
    const r = Math.round(totalR / pixelCount);
    const g = Math.round(totalG / pixelCount);
    const b = Math.round(totalB / pixelCount);
    const hexColor = rgbToHex(r, g, b);
    
    // Add if not duplicate
    if (!colors.includes(hexColor)) {
      onChange([...colors, hexColor]);
    }
  };
  
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    setCursorPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  
  const handleMouseLeave = () => {
    setCursorPos(null);
  };
  
  const removeColor = (colorToRemove: string) => {
    onChange(colors.filter(c => c !== colorToRemove));
  };
  
  const clearAll = () => {
    onChange([]);
  };
  
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pipette className="icon-sm text-glass" />
          <span className="text-sm font-medium text-glass-secondary font-body">
            Sample Colors
          </span>
        </div>
        {colors.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-glass-muted hover:text-red-500 flex items-center gap-1"
          >
            <RotateCcw className="icon-xs" /> Clear
          </button>
        )}
      </div>
      
      {/* Canvas and colors side by side */}
      <div className="flex gap-4">
        {/* Clickable image */}
        <div className="relative flex-shrink-0">
          <canvas
            ref={canvasRef}
            onClick={handleImageClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={`rounded-lg cursor-crosshair ${imageLoaded ? '' : 'hidden'}`}
            style={{ maxWidth: '200px', maxHeight: '150px' }}
          />
          
          {/* Cursor indicator */}
          {cursorPos && imageLoaded && (
            <div
              className="pointer-events-none absolute rounded-full border-2 border-white"
              style={{
                left: cursorPos.x - 12,
                top: cursorPos.y - 12,
                width: 24,
                height: 24,
                boxShadow: '0 0 0 2px rgba(0,0,0,0.5)'
              }}
            />
          )}
          
          {/* Loading state */}
          {!imageLoaded && (
            <div className="w-[200px] h-[150px] bg-slate-800/40 rounded-lg flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-slate-600 border-t-slate-500 rounded-full animate-spin" />
            </div>
          )}
        </div>
        
        {/* Sampled colors */}
        <div className="flex-1">
          {colors.length > 0 ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {colors.map((color, idx) => (
                  <div
                    key={idx}
                    className="relative group"
                  >
                    <div
                      className="w-10 h-10 rounded-lg shadow-md border-2 border-white"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                    <button
                      type="button"
                      onClick={() => removeColor(color)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                    {idx === 0 && (
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] bg-slate-700 text-white px-1 rounded">
                        1st
                      </span>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Color strip preview for multiples */}
              {colors.length > 1 && (
                <div className="h-4 rounded overflow-hidden flex shadow-inner">
                  {colors.map((color, idx) => (
                    <div
                      key={idx}
                      className="flex-1 h-full"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center">
              <p className="text-xs text-glass-muted font-body">
                Click on the photo to sample colors
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Tip */}
      <p className="text-xs text-glass-muted font-body">
        💡 For multichromes/thermals, sample multiple colors!
      </p>
    </div>
  );
}
